/**
 * ArtsMarketplace — /arts/marketplace
 * Global cross-artist marketplace. Buy paintings, prints, books,
 * albums, tickets and merchandise directly from artists.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Search } from "lucide-react";
import { http } from "@/lib/api";

export default function ArtsMarketplace() {
  const [data, setData] = useState({ total: 0, items: [], kinds: [] });
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: "48" });
        if (kind) params.set("kind", kind);
        if (q) params.set("q", q);
        const r = await http.get(`/arts/marketplace?${params}`).then(r => r.data);
        if (alive) setData(r);
      } catch {
        if (alive) setData({ total: 0, items: [], kinds: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [kind, q]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="arts-marketplace">
      <Link to="/arts" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Arts hub
      </Link>
      <div className="mb-6">
        <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>MARKETPLACE</div>
        <h1 className="font-display text-3xl md:text-4xl inline-flex items-center gap-2">
          <ShoppingBag className="w-7 h-7" /> Shop art from creators
        </h1>
        <p className="opacity-80 mt-1">
          Original paintings, prints, sculptures, books, albums and
          tickets — every purchase supports the artist directly.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border bg-transparent text-sm"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="arts-market-search"
          />
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="arts-market-kinds">
          <button type="button" onClick={() => setKind("")}
                  className={`text-xs px-3 py-1 rounded-full border ${!kind ? "bg-black text-white" : ""}`}
                  style={!kind ? undefined : { borderColor: "var(--wofy-border)" }}>All</button>
          {(data.kinds || []).map(k => (
            <button key={k} type="button" onClick={() => setKind(k)}
                    data-testid={`arts-market-kind-${k.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`text-xs px-3 py-1 rounded-full border ${kind === k ? "bg-black text-white" : "hover:bg-black/5"}`}
                    style={kind === k ? undefined : { borderColor: "var(--wofy-border)" }}>
              {k}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm opacity-70">Loading…</div>}
      {!loading && data.items.length === 0 && (
        <div className="wofy-card !p-6 opacity-80 text-center">
          <p>No products listed yet.</p>
          <p className="text-sm opacity-70 mt-1">
            Artists can add products from their profile page — look for the "Marketplace" tab.
          </p>
        </div>
      )}
      {!loading && data.items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="arts-market-grid">
          {data.items.map((p) => (
            <Link
              key={p.id}
              to={`/product/${p.id}`}
              data-testid={`arts-market-product-${p.id}`}
              className="group rounded-xl border overflow-hidden hover:bg-black/5 transition"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-full h-40 object-cover" />
              ) : (
                <div className="w-full h-40 bg-black/5" />
              )}
              <div className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                  {p.category || "Art"}
                </div>
                <div className="font-medium text-sm leading-tight line-clamp-2 group-hover:underline">
                  {p.name}
                </div>
                <div className="mt-1 flex items-center justify-between text-xs">
                  <span style={{ color: "var(--wofy-primary)" }} className="font-semibold">
                    {(p.currency || "usd").toUpperCase()} {p.price?.toFixed(2)}
                  </span>
                  <span className="opacity-60 truncate ml-2">
                    {p.business_name}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
